import {
  Database,
  Tables,
  TablesUpdate,
} from '@/lib/supabase/supabase_types_override';
import { supabase } from '@/lib/supabase/supabaseClient';
import { getUserSession } from './auth';
import { WithRequired } from '@/types/utils';

export const createProjectWithManager = async (
  data: Database['public']['Tables']['project']['Insert']
) => {
  const res = await supabase.from('project').insert(data).select('*').single();
  return res;
};

export const editProject = async (
  args: WithRequired<TablesUpdate<'project'>, 'id'>
) => {
  const { id, ...rest } = args;
  return await supabase.from('project').update(rest).eq('id', id);
};

export const editMultipleProjects = async ({
  projectIds,
  newData,
}: {
  projectIds: string[];
  newData: Database['public']['Tables']['project']['Update'];
}) => {
  return await supabase
    .from('project')
    .update(newData)
    .in('id', projectIds)
    .select();
};

export const editMultipleProjectsWithDifferentData = async ({
  data,
}: {
  data: Database['public']['Tables']['project']['Update'][];
}) => {
  return await supabase.from('project').upsert(data).select();
};

export const inviteUserToProject = async (
  projectId: string,
  invitedUserEmail: string
) => {
  const res = await supabase.functions.invoke(
    `rest/project/${projectId}/invite`,
    {
      body: JSON.stringify({
        invitedUserEmail,
      }),
      method: 'POST',
      headers: {
        origin: location.origin,
        dev: 'true',
      },
    }
  );
  // console.log(res)
  return res;
};

export const removeUserFromProject = async (
  project_id: string,
  user_id: string
) => {
  const res = await supabase
    .from('project_members')
    .delete()
    .eq('project_id', project_id)
    .eq('user_id', user_id)
    .select();
  return res;
};

export const getTeamName = async (project_id: string) => {
  const res = await supabase
    .from('project')
    .select('name')
    .eq('id', project_id)
    .single();
  return res;
};

export const getProject = async (project_id: string) => {
  const res = await supabase
    .from('project')
    .select(
      `
        *,
        organisation(
            organisation_members(*)
        )
        `
    )
    .eq('id', project_id)
    .single();
  if (res.error) {
    throw new Error(res.error.message);
  }
  return res.data;
};

export const projectsList = async () => {
  const res = await supabase
    .from('project')
    .select(
      `
        *,
        user!project_members(
            first_name,
            last_name,
            signed_up,
            id
        ),
        manager:user!project_manager_id_fkey(
            first_name,
            last_name
        ),
        organisation(
            organisation_members(*),
            name
        ),
        project_members(
            user(
                first_name,
                last_name,
                signed_up,
                id
            )
        ),
        crm_project(
            id,
            name
        )
    `
    )
    .order('created_at');
  // console.log(res)
  return res;
};

export const projectsListByOrgId = async (
  organisation_id: Tables<'organisation'>['id']
) => {
  const res = await supabase
    .from('project')
    .select(
      `
        *,
        user!project_members(
            first_name,
            last_name,
            signed_up,
            id,
            email
        ),
        manager:user!project_manager_id_fkey(
            first_name,
            last_name
        ),
        organisation(
            organisation_members(*),
            name
        ),
        project_members(
            user(
                first_name,
                last_name,
                signed_up,
                id,
                email
            )
        ),
        crm_project(
            id,
            name
        )
    `
    )
    .eq('organisation_id', organisation_id)
    .order('created_at');
  return res;
};

export const projectsListByOrgIdWithoutCrm = async (
  organisation_id: string
) => {
  const res = await supabase
    .from('project')
    .select(
      `
        name,
        id
    `
    )
    .eq('organisation_id', organisation_id)
    .is('crm_id', null)
    .order('created_at');
  return res;
};

export const externalProjectsList = async () => {
  const {
    data: { session },
    error: userError,
  } = await getUserSession();
  if (userError) throw userError;
  const {
    user: { id: userId },
  } = session!;
  const { data: organizationsData, error: organizationIdsError } =
    await supabase
      .from('organisation_members')
      .select('organisation_id')
      .eq('user_id', userId);
  if (organizationIdsError) throw organizationIdsError;
  const organizationIdsRaw = organizationsData.map(
    ({ organisation_id }) => organisation_id
  );
  const res = await supabase
    .from('project')
    .select(
      `
        *,
        user!project_members(
            first_name,
            last_name,
            signed_up,
            id
        ),
        manager:user!project_manager_id_fkey(
            first_name,
            last_name
        ),
        organisation(
            organisation_members(*),
            name
        ),
        project_members(
            user(
                first_name,
                last_name,
                signed_up,
                id
            )
        ),
        crm_project(
            id,
            name
        )
    `
    )
    .not('organisation_id', 'in', `(${organizationIdsRaw.join(',')})`)
    .order('created_at');
  return res;
};

export const getOrganizationProjects = async (organisation_id: number) => {
  const res = await supabase
    .from('project')
    .select('*, manager:manager_id(*), crm_project(*)')
    .eq('organisation_id', organisation_id)
    .order('created_at');
  return res;
};

export const getProjectById = async (project_id: string) => {
  const res = await supabase
    .from('project')
    .select(`*, project_members(*), crm_project(*,crm(*,crm_config(*)))`)
    .eq('id', project_id)
    .single();
  return res;
};

export const archiveProject = async (project_id: string) => {
  return await supabase
    .from('project')
    .update({ archived: true })
    .eq('id', project_id)
    .select();
};

export const retrieveProject = async (project_id: string) => {
  return await supabase
    .from('project')
    .update({ archived: false })
    .eq('id', project_id)
    .select();
};

export const deleteProject = async (project_id: string) => {
  return await supabase.from('project').delete().eq('id', project_id);
};

export const getProjectMembers = async (project_id: string) => {
  return await supabase
    .from('project_members')
    .select('user(*)')
    .eq('project_id', project_id);
};

export const getProjectDataWithCrm = async (project_id: string) => {
  return await supabase
    .from('project')
    .select(
      `
      crm_project:crm_project_id (
        *
      ),
      crm:crm_id (*)
    `
    )
    .eq('id', project_id)
    .single();
};
